/* Stephen Milner Photography — "See it on your wall" (self-hosted AR).
 *
 * Reads the smpar.models JSON embedded by blocks/smp-ar.liquid, resolves the
 * entry for the currently selected Frame|Size, and either launches native AR
 * (iOS Quick Look / Android Scene Viewer via <model-viewer>) or opens a
 * desktop modal with an interactive 3D view + a locally generated QR code
 * that hands off to the phone (product URL + #ar).
 *
 * Design constraints honoured here:
 *  - model-viewer + QR libraries are lazy-loaded (never on initial page load).
 *  - The theme (Horizon) morphs the product DOM on variant change, so all
 *    listeners are delegated to document and all state is re-read fresh.
 *  - The modal + hidden AR launcher live on document.body, outside the
 *    morphed <main>, so they survive variant changes.
 *  - No external services beyond the two library CDNs; QR is generated
 *    entirely in the browser.
 */

const MV_FALLBACK = 'https://unpkg.com/@google/model-viewer@4.3.1/dist/model-viewer.min.js';
/* Self-hosted PATCHED model-viewer (see assets/smp-model-viewer.min.js):
 * stock 4.3.1 ignores ar-placement for iOS Quick Look and anchors the
 * generated USDZ to the FLOOR; the patch anchors it to the WALL. The URL
 * comes from the data-mv-src attribute set by snippets/smp-ar.liquid. */
function mvSrc() {
  return arRoot()?.dataset.mvSrc || MV_FALLBACK;
}
const QR_SRC = 'https://unpkg.com/qrcode-generator@1.4.4/qrcode.js';
const LOAD_TIMEOUT_MS = 20000;

/* iOS browsers other than Safari (Chrome/Edge/Firefox/Google app/DuckDuckGo —
 * all WebKit shells) CAN launch AR Quick Look from the USDZ blob that
 * model-viewer generates, but only when the blob URL carries no #fragment:
 * with ar-scale="fixed" model-viewer appends #allowsContentScaling=0 and the
 * launch degrades to a flat file preview instead of the camera (Chromium bug
 * 1197345 / model-viewer discussion #2898). Those browsers get
 * ar-scale="auto" — the model is authored at true metres, so it still
 * APPEARS at true size; only the pinch-scale lock is lost. Safari keeps the
 * hard lock. Browser list mirrors the bundle's own Quick Look candidacy
 * check (smp-model-viewer.min.js) — keep the two in sync. */
const IS_IOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const IS_IOS_NON_SAFARI = IS_IOS
  && /CriOS\/|EdgiOS\/|FxiOS\/|GSA\/|DuckDuckGo\//.test(navigator.userAgent);

/* ---------------------------------------------------------------- loaders */

let mvPromise = null;
function loadModelViewer() {
  if (customElements.get('model-viewer')) return Promise.resolve();
  if (!mvPromise) {
    mvPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.type = 'module';
      s.src = mvSrc();
      s.onload = () => customElements.whenDefined('model-viewer').then(resolve, reject);
      s.onerror = () => { mvPromise = null; reject(new Error('model-viewer failed to load')); };
      document.head.appendChild(s);
    });
  }
  return mvPromise;
}

let qrPromise = null;
function loadQr() {
  if (window.qrcode) return Promise.resolve(window.qrcode);
  if (!qrPromise) {
    qrPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = QR_SRC;
      s.onload = () => (window.qrcode ? resolve(window.qrcode) : reject(new Error('qrcode global missing')));
      s.onerror = () => { qrPromise = null; reject(new Error('qrcode failed to load')); };
      document.head.appendChild(s);
    });
  }
  return qrPromise;
}

/* ------------------------------------------------------------- data reads */

function arRoot() {
  return document.querySelector('[data-smp-ar]');
}

function modelsData() {
  const el = arRoot()?.querySelector('[data-smp-ar-models]');
  if (!el) return null;
  try { return JSON.parse(el.textContent); } catch { return null; }
}

function slugFrame(value) {
  return value.trim().toLowerCase().replace(/\s+/g, '-');
}

function slugSize(value) {
  const m = value.match(/(\d+)\s*[×x]\s*(\d+)\s*cm/i);
  return m ? `${m[1]}x${m[2]}` : null;
}

/** Current option selections, read fresh from the variant picker DOM. */
function selection() {
  const out = {};
  document.querySelectorAll('variant-picker fieldset input:checked').forEach((input) => {
    const name = (input.dataset.optionName || '').trim().toLowerCase();
    if (name) out[name] = input.value;
  });
  return out;
}

/** Debug override: ?smpartest=<encoded Shopify-CDN glb URL> swaps the model
 *  through the exact same flow — used to A/B QuickLook material variants on a
 *  real phone. Shopify CDN only; ignored otherwise. */
function testOverride() {
  try {
    const raw = new URLSearchParams(window.location.search).get('smpartest');
    if (!raw) return null;
    const url = new URL(raw, window.location.origin);
    return url.hostname === 'cdn.shopify.com' ? url.toString() : null;
  } catch { return null; }
}

/** Resolve the model entry for the current Frame|Size (with fallbacks). */
function currentEntry() {
  const data = modelsData();
  if (!data || !data.models) return null;
  const sel = selection();
  let key = null;
  if (sel.frame && sel.size) {
    const f = slugFrame(sel.frame);
    const s = slugSize(sel.size);
    if (f && s) {
      // media|frame|size (2026-08-17: prints and acrylics can share a size
      // label, so the media disambiguates); legacy frame|size still resolves.
      if (sel.media) {
        const m = slugFrame(sel.media);
        key = `${m}|${f}|${s}`;
        if (!data.models[key]) key = `${f}|${s}`;
      } else {
        key = `${f}|${s}`;
      }
    }
  }
  let entry = key ? data.models[key] : null;
  // If the customer has chosen a specific Frame and Size and there is no model
  // for it, STOP. The old code fell through to defaultKey, which put a 107x60
  // piece on the wall for someone who had selected 53x30 — silently wrong at
  // the one job AR exists to do, which is judge scale. No model means no
  // button (see syncAvailability); it never means a different size.
  if (!entry && key) return null;
  if (!entry && data.defaultKey) { key = data.defaultKey; entry = data.models[key]; }
  if (!entry) {
    const first = Object.keys(data.models)[0];
    if (first) { key = first; entry = data.models[first]; }
  }
  if (!entry || !entry.glb) return null;
  const override = testOverride();
  return Object.assign({ key }, entry, override ? { glb: override } : null);
}

/** Honest print size (cm) parsed from the entry key — the framed artwork
 *  itself; entry w_cm/h_cm include the shadow margin and are not shown. */
function printSize(entry) {
  const m = (entry.key || '').match(/(\d+)x(\d+)$/);
  return m ? `${m[1]} × ${m[2]} cm` : null;
}

/* ------------------------------------------------------------ AR launcher */

let launcher = null;
function getLauncher() {
  if (launcher && document.body.contains(launcher)) return launcher;
  launcher = document.createElement('model-viewer');
  launcher.setAttribute('ar', '');
  launcher.setAttribute('ar-modes', 'webxr scene-viewer quick-look');
  launcher.setAttribute('ar-scale', IS_IOS_NON_SAFARI ? 'auto' : 'fixed');
  launcher.setAttribute('ar-placement', 'wall');
  launcher.setAttribute('shadow-intensity', '0');
  launcher.setAttribute('loading', 'eager');
  launcher.className = 'smp-ar__launcher';
  document.body.appendChild(launcher);
  return launcher;
}

function whenModelReady(mv) {
  return new Promise((resolve, reject) => {
    if (mv.loaded) return resolve();
    const timer = setTimeout(() => reject(new Error('model load timeout')), LOAD_TIMEOUT_MS);
    mv.addEventListener('load', () => { clearTimeout(timer); resolve(); }, { once: true });
    mv.addEventListener('error', (e) => { clearTimeout(timer); reject(e); }, { once: true });
  });
}

/** Wait for model-viewer to finish computing AR capability. canActivateAR is
 *  set during the element's async update cycle — reading it after a single
 *  frame races that cycle and wrongly reports false on real iPhones. */
async function arCapable(mv) {
  if (mv.updateComplete) await mv.updateComplete.catch(() => {});
  if (mv.canActivateAR) return true;
  for (let i = 0; i < 8; i++) {                     // up to ~800ms grace
    await new Promise((r) => setTimeout(r, 100));
    if (mv.canActivateAR) return true;
  }
  return false;
}

/** Try to launch native AR for the entry. Resolves false when the device
 *  can't do AR (caller falls back to the modal). */
/* --------------------------------------------------------- preflight sheet */

/** Instruction step between the button tap and the camera opening. Shown ONLY
 *  when native AR is confirmed available. The model preloads while the
 *  customer reads; "Open camera" enables once it's ready, and its click
 *  carries the user gesture straight into activateAR. */
let preflight = null;
function getPreflight() {
  if (preflight && document.body.contains(preflight)) return preflight;
  preflight = document.createElement('div');
  preflight.className = 'smp-ar-preflight';
  preflight.innerHTML = `
    <div class="smp-ar-preflight__backdrop" data-smp-ar-pf-close></div>
    <div class="smp-ar-preflight__panel" role="dialog" aria-modal="true" aria-label="Before your camera opens">
      <button type="button" class="smp-ar-preflight__close" data-smp-ar-pf-close aria-label="Close">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M2 2l12 12M14 2L2 14"/></svg>
      </button>
      <p class="smp-ar-preflight__title">See it on your wall</p>
      <ul class="smp-ar-preflight__steps">
        <li>Aim where your wall meets the floor.</li>
        <li>Move your phone slowly from side to side.</li>
        <li>If the piece lands on the floor, drag it up onto your wall.</li>
        <li>Good light helps. Stand back a little.</li>
      </ul>
      <button type="button" class="button smp-ar-preflight__go" data-smp-ar-pf-go data-loading>
        <span>Open camera</span>
        <span class="smp-ar__spinner smp-ar-preflight__go-spinner" aria-hidden="true"></span>
      </button>
    </div>`;
  document.body.appendChild(preflight);
  return preflight;
}

function closePreflight() {
  if (!preflight) return;
  preflight.removeAttribute('open');
  document.removeEventListener('keydown', onPreflightKeydown, true);
  if (lastFocus && document.contains(lastFocus)) lastFocus.focus();
  lastFocus = null;
}

function onPreflightKeydown(e) {
  if (e.key === 'Escape') { e.preventDefault(); closePreflight(); }
}

/** Show the sheet, resolve when the customer launches AR or cancels.
 *  Resolves true when AR launched, false when it needs the modal fallback. */
function runPreflight(mv, entry) {
  return new Promise((resolve) => {
    const sheet = getPreflight();
    const go = sheet.querySelector('[data-smp-ar-pf-go]');
    go.setAttribute('data-loading', '');
    go.setAttribute('aria-disabled', 'true');
    let settled = false;
    const onClick = async (e) => {
      if (e.target.closest('[data-smp-ar-pf-close]')) { finish(true); return; }   // cancel: do nothing more
      const goBtn = e.target.closest('[data-smp-ar-pf-go]');
      if (!goBtn || goBtn.hasAttribute('data-loading')) return;
      try { await mv.activateAR(); finish(true); }
      catch { finish(false); }
    };
    const finish = (v) => {
      if (settled) return;
      settled = true;
      sheet.removeEventListener('click', onClick);
      closePreflight();
      resolve(v);
    };

    whenModelReady(mv).then(
      () => { go.removeAttribute('data-loading'); go.removeAttribute('aria-disabled'); },
      () => finish(false),                       // model failed: 3D modal fallback
    );
    sheet.addEventListener('click', onClick);

    lastFocus = document.activeElement;
    sheet.setAttribute('open', '');
    mainBusy(false);               // the sheet's own button carries the state now
    document.addEventListener('keydown', onPreflightKeydown, true);
    go.focus();
  });
}

async function launchAr(entry) {
  await loadModelViewer();
  const mv = getLauncher();
  // src goes on BEFORE the capability check: some model-viewer builds only
  // report canActivateAR once a model is attached, and the download overlaps
  // the preflight sheet (the modal fallback reuses the cached GLB either way).
  if (mv.getAttribute('src') !== entry.glb) {
    mv.setAttribute('alt', entry.alt || '');
    mv.setAttribute('src', entry.glb);
  }
  if (!(await arCapable(mv))) return false;
  return runPreflight(mv, entry);
}

/* ------------------------------------------------------------------ modal */

let modal = null;
let lastFocus = null;

function getModal() {
  if (modal && document.body.contains(modal)) return modal;
  modal = document.createElement('div');
  modal.className = 'smp-ar-modal';
  modal.innerHTML = `
    <div class="smp-ar-modal__backdrop" data-smp-ar-close></div>
    <div class="smp-ar-modal__panel" role="dialog" aria-modal="true" aria-label="See it on your wall">
      <button type="button" class="smp-ar-modal__close" data-smp-ar-close aria-label="Close">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M2 2l12 12M14 2L2 14"/></svg>
      </button>
      <div class="smp-ar-modal__viewer" data-smp-ar-viewer></div>
      <div class="smp-ar-modal__side">
        <p class="smp-ar-modal__title" data-smp-ar-title></p>
        <p class="smp-ar-modal__size" data-smp-ar-size></p>
        <p class="smp-ar-modal__hint">Drag to rotate the piece.</p>
        <div class="smp-ar-modal__qr" data-smp-ar-qr-wrap hidden>
          <div class="smp-ar-modal__qr-code" data-smp-ar-qr></div>
          <p class="smp-ar-modal__qr-copy">Point your phone camera at the code to see this piece on your own wall.</p>
        </div>
      </div>
    </div>`;
  modal.addEventListener('click', (e) => {
    if (e.target.closest('[data-smp-ar-close]')) closeModal();
  });
  document.body.appendChild(modal);
  return modal;
}

function onModalKeydown(e) {
  if (e.key === 'Escape') { e.preventDefault(); closeModal(); return; }
  if (e.key !== 'Tab' || !modal) return;
  const focusables = modal.querySelectorAll('button, [href], model-viewer, [tabindex]:not([tabindex="-1"])');
  if (!focusables.length) return;
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
}

function closeModal() {
  if (!modal) return;
  modal.removeAttribute('open');
  document.removeEventListener('keydown', onModalKeydown, true);
  document.documentElement.classList.remove('smp-ar-no-scroll');
  const mv = modal.querySelector('model-viewer');
  if (mv) mv.removeAttribute('auto-rotate');
  if (lastFocus && document.contains(lastFocus)) lastFocus.focus();
  lastFocus = null;
}

function modalViewer() {
  const host = getModal().querySelector('[data-smp-ar-viewer]');
  let mv = host.querySelector('model-viewer');
  if (!mv) {
    mv = document.createElement('model-viewer');
    mv.setAttribute('camera-controls', '');
    mv.setAttribute('shadow-intensity', '0');
    mv.setAttribute('ar-scale', 'fixed');
    mv.setAttribute('loading', 'eager');
    mv.setAttribute('interaction-prompt', 'none');
    // The model is a flat panel (true for wall AR, where it's only ever seen
    // head-on). Clamp the orbit to a gentle front arc so the modal can't
    // reveal the edge-on flatness or the mirrored back face.
    mv.setAttribute('camera-orbit', '0deg 90deg auto');
    mv.setAttribute('min-camera-orbit', '-35deg 70deg auto');
    mv.setAttribute('max-camera-orbit', '35deg 110deg auto');
    mv.setAttribute('disable-pan', '');
    host.appendChild(mv);
  }
  return mv;
}

async function renderQr() {
  const wrap = getModal().querySelector('[data-smp-ar-qr-wrap]');
  const target = getModal().querySelector('[data-smp-ar-qr]');
  try {
    const qrcode = await loadQr();
    const url = new URL(window.location.href);
    url.hash = 'ar';
    const qr = qrcode(0, 'M');
    qr.addData(url.toString());
    qr.make();
    target.innerHTML = qr.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
    const svg = target.querySelector('svg');
    if (svg) { svg.setAttribute('role', 'img'); svg.setAttribute('aria-label', 'QR code linking to this page for AR viewing on your phone'); }
    wrap.hidden = false;
  } catch {
    wrap.hidden = true; // QR is an enhancement; the 3D view still works
  }
}

async function openModal(entry) {
  await loadModelViewer();
  const m = getModal();
  const root = arRoot();
  m.querySelector('[data-smp-ar-title]').textContent = root?.dataset.productTitle || '';
  const size = printSize(entry);
  m.querySelector('[data-smp-ar-size]').textContent = size ? `${size} — shown at true size in AR` : '';
  const mv = modalViewer();
  if (mv.getAttribute('src') !== entry.glb) {
    mv.setAttribute('alt', entry.alt || '');
    mv.setAttribute('src', entry.glb);
  }
  lastFocus = document.activeElement;
  m.setAttribute('open', '');
  document.documentElement.classList.add('smp-ar-no-scroll');
  document.addEventListener('keydown', onModalKeydown, true);
  // Phones get the 3D view with no browser-redirect notice; the QR hand-off
  // is desktop-only (pointing a phone at its own screen is useless).
  if (!/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) renderQr();
  m.querySelector('.smp-ar-modal__close').focus();
}

/* ------------------------------------------------------------- open logic */

let busy = false;

/** Toggle the page button's busy state. Always queried fresh — the theme
 *  morphs the product DOM, so a remembered node can be a detached stale copy
 *  while a clean clone sits in the page. */
function mainBusy(on) {
  document.querySelectorAll('[data-smp-ar-open]').forEach((b) => {
    if (on) { b.setAttribute('data-busy', ''); b.setAttribute('aria-disabled', 'true'); }
    else { b.removeAttribute('data-busy'); b.removeAttribute('aria-disabled'); }
  });
}

async function onOpen() {
  if (busy) return;
  const entry = currentEntry();
  if (!entry) return;
  busy = true;
  mainBusy(true);
  try {
    const launched = await launchAr(entry).catch(() => false);
    if (!launched) await openModal(entry);
  } catch { /* both paths failed: leave page untouched */ }
  finally {
    busy = false;
    mainBusy(false);
  }
}

/* Safety net: iOS suspends page JS while AR Quick Look is open, which can
 * strand the busy state. On any return to the page, clear it unless a flow
 * is genuinely still running an overlay. */
function recoverBusy() {
  const overlayOpen = (preflight && preflight.hasAttribute('open')) || (modal && modal.hasAttribute('open'));
  if (!overlayOpen) { busy = false; mainBusy(false); }
}
window.addEventListener('pageshow', recoverBusy);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') recoverBusy();
});

/* Show the button only for a selection we actually have a model for. A dead
 * button that does nothing when tapped is worse than no button at all. */
function syncAvailability() {
  const root = arRoot();
  if (!root) return;
  const ok = !!currentEntry();
  // The `hidden` attribute alone does nothing here: .smp-ar carries
  // `display: block` from the theme's stylesheet, which beats the user-agent
  // `[hidden] { display: none }` rule. Set the inline style too — verified in
  // the browser, where hidden was true and the button was still on screen.
  root.hidden = !ok;
  root.style.display = ok ? '' : 'none';
}

/* Delegated events — survive theme DOM morphs. */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-smp-ar-open]');
  if (btn) { e.preventDefault(); onOpen(btn); }
});

document.addEventListener('change', (e) => {
  if (e.target instanceof HTMLInputElement && e.target.closest('variant-picker')) {
    syncAvailability();
  }
});
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncAvailability);
} else {
  syncAvailability();
}

/* Keep an open modal in sync when the customer changes Frame/Size. */
document.addEventListener('change', (e) => {
  if (!(e.target instanceof HTMLInputElement)) return;
  if (!e.target.closest('variant-picker')) return;
  if (!modal || !modal.hasAttribute('open')) return;
  const entry = currentEntry();
  if (!entry) return;
  const mv = modalViewer();
  if (mv.getAttribute('src') !== entry.glb) {
    mv.setAttribute('alt', entry.alt || '');
    mv.setAttribute('src', entry.glb);
  }
  const size = printSize(entry);
  getModal().querySelector('[data-smp-ar-size]').textContent = size ? `${size} — shown at true size in AR` : '';
});

/* Pre-warm model-viewer once the block scrolls into view (script only). */
(function preWarm() {
  const el = arRoot();
  if (!el || !('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver((entries) => {
    if (entries.some((x) => x.isIntersecting)) {
      io.disconnect();
      loadModelViewer().catch(() => {});
    }
  }, { rootMargin: '200px' });
  io.observe(el);
})();

/* Desktop QR hand-off: #ar (or ?ar=1) auto-launches for the current selection. */
(function deepLink() {
  const wants = window.location.hash === '#ar' || new URLSearchParams(window.location.search).get('ar') === '1';
  if (!wants) return;
  const kick = () => {
    const btn = document.querySelector('[data-smp-ar-open]');
    if (btn) onOpen(btn);
  };
  // Give the theme a beat to hydrate the variant picker before resolving keys.
  setTimeout(kick, 300);
})();
